// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RpcApiResponse } from '@interfaces/api.interfaces';
import { from, lastValueFrom, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../login/services/auth.service';
import { getDealerHomePage } from '../utils/utils';
import { ErrorService } from './error.service';
import { RequestService } from './request.service';

@Injectable({ providedIn: 'root' })
export class ApiService {
    constructor(
        private readonly authService: AuthService,
        private readonly requestService: RequestService,
        private readonly errorService: ErrorService,
        private readonly router: Router
    ) {}

    apiErrorHandler(httpResponse: HttpErrorResponse): HttpErrorResponse {
        if (httpResponse.status === 0) {
            // Could not connect to api server
            this.errorService.showWhitelistError();
        } else if (httpResponse.status === 401) {
            this.authService.goToLogin();
        } else if (httpResponse.status === 404 && httpResponse?.error?.name === 'invalidUser') {
            this.handleInvalidUserError(httpResponse);
            // Custom Exception from api. Forward to calling component/service.
        } else if (httpResponse.status === 550) {
            //return httpResponse;
            this.errorService.showError(httpResponse.error);
            //Custom Access Denied Will route to dealer ddj home page on close.
        } else if (httpResponse.status === 450) {
            const parsedError = JSON.parse(httpResponse.error?.message);
            httpResponse.error.message = parsedError.message;
            this.errorService
                .showErrorAtService(httpResponse.error)
                .afterClosed()
                .subscribe((val) => {
                    this.router.navigate(getDealerHomePage(parsedError.defaultPageId, parsedError.isCustomer, parsedError.permissions));
                });
        } else {
            this.errorService.showError(httpResponse.error);
        }
        return httpResponse;
    }

    handleInvalidUserError(httpResponse: HttpErrorResponse): any {
        if (httpResponse.status === 404 && httpResponse?.error?.name === 'invalidUser') {
            this.errorService
                .showErrorAtService({
                    title: "You've Been Logged Out",
                    message:
                        'Your account has been unauthenticated. Please login again to continue. If you cannot log in please contact your system administrator.',
                })
                .afterClosed()
                .subscribe(() => {
                    this.authService.signOut();
                    this.authService.goToLogin();
                });
        }
        return httpResponse;
    }

    async checkUser() {
        const response = await lastValueFrom(this.authService.isAuthenticated());

        if (!response) {
            this.authService.signOut();
            this.authService.goToLogin();
        }

        return response;
    }

    // GET without error handling
    getV1$<T>(path: string, adminDealerId?: number, authenticated = true): Observable<RpcApiResponse<T>> {
        const authToken = authenticated ? this.authService.token : null;

        if (adminDealerId) {
            path += `?adminDealerId=${adminDealerId}`;
        }

        //check the user if requires authentication only as it will signout and route to login if user not authenticated
        const pipelineStart = authenticated ? from(this.checkUser()) : of(true);
        return pipelineStart.pipe(
            filter((isAuthorized) => !authenticated || isAuthorized),
            switchMap(() => this.requestService.get$<T>(path, authToken))
        );
    }

    // GET with error handling.
    getV2$<T>(path: string, adminDealerId?: number, authenticated = true): Observable<RpcApiResponse<T>> {
        return this.getV1$<T>(path, adminDealerId, authenticated).pipe(
            catchError((httpResponse) => {
                this.apiErrorHandler(httpResponse);
                return of(httpResponse);
            })
        );
    }

    postUnauthenticatedV2<T>(path: string, body: any): Observable<RpcApiResponse<T>> {
        return this.requestService.post$<T>(path, body).pipe(catchError((httpResponse) => of(this.apiErrorHandler(httpResponse))));
    }

    putUnauthenticatedV2<T>(path: string, body: any): Observable<RpcApiResponse<T>> {
        return this.requestService.put$<T>(path, body).pipe(catchError((httpResponse) => of(this.apiErrorHandler(httpResponse))));
    }

    getUi$<T>(path: string, uiPage: string): Observable<RpcApiResponse<T>> {
        if (uiPage) {
            path += `?type=${uiPage}`;
        }

        return from(this.checkUser()).pipe(
            filter((isAuthorized) => isAuthorized),
            switchMap(() => this.requestService.get$<T>(path, this.authService.token)),
            catchError((httpResponse) => {
                this.apiErrorHandler(httpResponse);
                return httpResponse;
            })
        );
    }

    getBuffer$<T>(path: string): Observable<any> {
        return from(this.checkUser()).pipe(
            filter((isAuthorized) => isAuthorized),
            switchMap(() => this.requestService.get$<T>(path, this.authService.token, { headers: { accept: '*/*' } })),
            catchError((httpResponse) => {
                this.apiErrorHandler(httpResponse);
                return httpResponse;
            }),
            map((response) => {
                const res = this.transformBase64Content(response);
                return res;
            })
        );
    }

    getSignedBlob$<T>(path: string): Observable<Blob> {
        const requestOptions: object = {
            responseType: 'blob',
        };
        return this.requestService.get$<T>(path, null, requestOptions).pipe(
            catchError((httpResponse) => of(this.apiErrorHandler(httpResponse))),
            map((data: any) => data)
        );
    }

    deleteV2$<T>(path: string, body?): Observable<RpcApiResponse<T>> {
        return from(this.checkUser()).pipe(
            filter((isAuthorized) => isAuthorized),
            switchMap(() =>
                this.requestService.delete$<T>(path, this.authService.token, {
                    body: body,
                })
            ),
            catchError((httpResponse) => {
                this.apiErrorHandler(httpResponse);
                return httpResponse;
            })
        );
    }

    deleteV3$<T>(path: string, body?): Observable<RpcApiResponse<T>> {
        return from(this.checkUser()).pipe(
            filter((isAuthorized) => isAuthorized),
            switchMap(() =>
                this.requestService.delete$<T>(path, this.authService.token, {
                    body: body,
                })
            ),
            catchError((httpResponse) => {
                throw this.apiErrorHandler(httpResponse);
            })
        );
    }

    postV2$<T>(path: string, body, headers?: any): Observable<RpcApiResponse<T>> {
        return from(this.checkUser()).pipe(
            filter((isAuthorized) => isAuthorized),
            switchMap(() =>
                this.requestService.post$<T>(path, body, this.authService.token, {
                    headers,
                })
            ),
            catchError((httpResponse) => {
                this.apiErrorHandler(httpResponse);
                return of(httpResponse);
            })
        );
    }

    //This version rethrows errors for custom error handling. Use handleErrors to enable/disable generic error handling
    postV3$<T>(path: string, body, headers?: any, handleErrors = true): Observable<RpcApiResponse<T>> {
        return from(this.checkUser()).pipe(
            filter((isAuthorized) => isAuthorized),
            switchMap(() =>
                this.requestService.post$<T>(path, body, this.authService.token, {
                    headers,
                })
            ),
            catchError((httpResponse) => {
                throw handleErrors ? this.apiErrorHandler(httpResponse) : httpResponse;
            })
        );
    }

    putV2$<T>(path: string, body, headers?: any, authenticated = true): Observable<RpcApiResponse<T>> {
        //check the user if requires authentication only as it will signout and route to login if user not authenticated
        const pipelineStart = authenticated ? from(this.checkUser()) : of(true);
        return pipelineStart.pipe(
            filter((isAuthorized) => !authenticated || isAuthorized),
            switchMap(() => this.requestService.put$<T>(path, body, this.authService.token, { headers })),
            catchError((httpResponse) => {
                this.apiErrorHandler(httpResponse);
                return of({ ...httpResponse });
            })
        );
    }

    //rethrows error for custom error handling
    putV3$<T>(path: string, body, headers?: any, authenticated = true): Observable<RpcApiResponse<T>> {
        const pipelineStart = authenticated ? from(this.checkUser()) : of(true);
        return pipelineStart.pipe(
            filter((isAuthorized) => !authenticated || isAuthorized),
            switchMap(() => this.requestService.put$<T>(path, body, this.authService.token, { headers })),
            catchError((httpResponse) => {
                throw this.apiErrorHandler(httpResponse);
            })
        );
    }

    s3put$<T>(path: string, body): Observable<RpcApiResponse<T>> {
        return from(this.checkUser()).pipe(
            filter((isAuthorized) => isAuthorized),
            switchMap(() => this.requestService.put$<T>(path, body)),
            catchError((httpResponse) => {
                this.apiErrorHandler(httpResponse);
                return of({ ...httpResponse });
            })
        );
    }

    transformBase64Body(file) {
        const byteCharacters = atob(file.data.Body);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const binaryData = [];
        binaryData.push(byteArray);
        file.data.Body = binaryData;
        file.data.fileName = file.data.ContentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
        return file;
    }

    transformBase64Content(file) {
        const byteCharacters = atob(file.data.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        //let binaryData = [];
        //binaryData.push(byteArray);
        file.data.Body = {
            data: byteArray,
            type: 'Buffer',
        };
        return file;
    }

    transformBase64Stream(file) {
        const byteCharacters = atob(file.body.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const binaryData = [];
        binaryData.push(byteArray);
        file.body.data = binaryData;
        file.body.data.fileName = file.headers.get('ResponseContentDisposition').split(';')[1].split('filename')[1].split('=')[1].trim();

        return file;
    }
}
